import { Link } from 'react-router-dom';
import { scr } from '../../lib/api';
import { useApiQuery } from '../../lib/useApi';
import type { AnchorsResponse, BudgetLinesResponse, Dataset, Page, PublicNotice } from '../../lib/types';
import { StatCard } from '../../ui/components';

interface ExploreCard {
  to: string;
  title: string;
  blurb: string;
}

const CARDS: ExploreCard[] = [
  {
    to: '/datasets',
    title: 'Open Data Catalogue',
    blurb: 'Browse every dataset published by government ministries and departments, with live JSON/CSV downloads.',
  },
  {
    to: '/budget',
    title: 'Budget Explorer',
    blurb: 'See how the national budget is allocated, revised, and spent by ministry, department and programme.',
  },
  {
    to: '/foi',
    title: 'Freedom of Information',
    blurb: 'Submit a request under the Access to Information Act 2018 — anonymously if you prefer — and track it.',
  },
  {
    to: '/notices',
    title: 'Public Notices',
    blurb: 'Gazette notices, tenders, planning applications and licences published across government, in one feed.',
  },
  {
    to: '/parcels',
    title: 'My Parcel',
    blurb: 'Look up a cadastral land parcel by its number or by map coordinates.',
  },
  {
    to: '/integrity',
    title: 'Platform Integrity',
    blurb: 'The daily cryptographic anchors that make every module’s audit trail independently verifiable.',
  },
];

export function Home() {
  const datasetsQ = useApiQuery<Page<Dataset>>('/api/v1/opengov/datasets?limit=100');
  const budgetQ = useApiQuery<BudgetLinesResponse>('/api/v1/opengov/budget-lines?limit=1');
  const noticesQ = useApiQuery<Page<PublicNotice>>('/api/v1/opengov/notices?limit=100');
  const anchorsQ = useApiQuery<AnchorsResponse>('/api/v1/opengov/anchors/latest');

  const datasetsCount = datasetsQ.data ? `${datasetsQ.data.items.length}${datasetsQ.data.nextCursor ? '+' : ''}` : '—';
  const noticesCount = noticesQ.data ? `${noticesQ.data.items.length}${noticesQ.data.nextCursor ? '+' : ''}` : '—';
  const anchorsCount = anchorsQ.data ? anchorsQ.data.items.length : '—';

  return (
    <div>
      <section className="mb-10 rounded-2xl border border-slate-200 bg-gradient-to-br from-brand-50 to-white px-6 py-10 sm:px-10">
        <h1 className="max-w-2xl text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
          Government, in the open.
        </h1>
        <p className="mt-3 max-w-2xl text-base text-slate-600">
          OpenGov Seychelles is the national transparency portal: published budgets, open datasets, Freedom of
          Information requests, and public notices from every ministry, department and agency — all in one place,
          free to browse without an account.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/datasets" className="btn-primary">
            Browse open data
          </Link>
          <Link to="/foi" className="btn-secondary">
            Submit an FOI request
          </Link>
        </div>
      </section>

      <div className="mb-10 grid gap-3 sm:grid-cols-4">
        <StatCard label="Datasets published" value={datasetsCount} hint="Across all ministries & departments" />
        <StatCard
          label="Budget lines tracked"
          value={budgetQ.data ? scr(budgetQ.data.totals.revised) : '—'}
          hint="Total revised allocation, all years"
        />
        <StatCard label="Public notices" value={noticesCount} hint="Gazette, tenders, planning, licences" />
        <StatCard label="Audit anchors" value={anchorsCount} hint="Daily integrity proofs, all modules" />
      </div>

      <h2 className="mb-4 text-lg font-bold text-slate-900">Explore the portal</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="card flex flex-col gap-2 p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
          >
            <h3 className="text-base font-bold text-slate-900">{c.title}</h3>
            <p className="text-sm text-slate-500">{c.blurb}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
